import React from "react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";

// Neutral answers — no unverified claims. All signposted back to a conversation with an adviser.
// Wording aligns with HL Partnership AR permissions: we advise on mortgages
// and protection; any service outside that scope is signposted as a
// third-party referral.
const FAQS = [
    {
        q: "What kinds of finance do you advise on?",
        a: "Acumen Financial Services Ltd is a mortgage and protection network — we advise on residential mortgages, remortgages, first-time buyer, buy-to-let, and protection & insurance. Services outside these permissions — including commercial finance, business loans, non-residential bridging and growth planning — are provided by way of a third-party referral to a suitable specialist.",
    },
    {
        q: "Are you authorised and regulated?",
        a: "Acumen Financial Services Ltd (FRN: 1001151) is an appointed representative of HL Partnership Limited, which is authorised and regulated by the Financial Conduct Authority under FCA number 303397. HL Partnership is the regulated firm; Acumen is authorised by HL Partnership to advise within the network's mortgage and protection permissions.",
    },
    {
        q: "How does the advice process work?",
        a: "Every client's situation is unique. We take the time to understand your circumstances, goals, and concerns before offering any recommendation, so the guidance is personalised, transparent and fully compliant.",
    },
    {
        q: "Do you offer protection and insurance?",
        a: "Yes — we advise on life insurance, critical illness cover, income protection, and home and landlord insurance under HL Partnership. Private medical insurance and estate planning are offered by way of a third-party referral.",
    },
    {
        q: "Can you help with business or commercial finance?",
        a: "Business loans, commercial mortgages, non-residential bridging and growth or expansion funding sit outside HL Partnership's regulated permissions and are handled by way of a third-party referral to a specialist commercial finance broker.",
    },
    {
        q: "How do I get started?",
        a: "Get in touch through the contact form. We'll arrange a conversation to understand what you're trying to achieve and outline how we can help.",
    },
];

export default function FAQ() {
    return (
        <section
            data-testid="faq-section"
            className="relative py-14 md:py-20 bg-brand-surfaceAlt/40 border-y border-brand-border"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-4">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-brand-accent" />
                            <span className="text-xs uppercase tracking-[0.24em] text-brand-accent font-semibold">
                                FAQ
                            </span>
                        </div>
                        <h2 className="font-serif-editorial text-4xl sm:text-5xl tracking-tight text-brand-primary leading-[1.05]">
                            Clear guidance,
                            <br />
                            <em className="italic font-light">confident choices.</em>
                        </h2>
                        <p className="mt-8 text-brand-muted leading-relaxed">
                            If your question isn't answered here,{" "}
                            <Link
                                to="/contact"
                                className="text-brand-primary link-underline"
                            >
                                get in touch
                            </Link>
                            {" "}and an adviser will respond.
                        </p>
                    </div>

                    <div className="md:col-span-7 md:col-start-6">
                        <Accordion
                            type="single"
                            collapsible
                            data-testid="faq-accordion"
                            className="w-full"
                        >
                            {FAQS.map((f, i) => (
                                <AccordionItem
                                    key={i}
                                    value={`item-${i}`}
                                    className="border-b border-brand-border"
                                >
                                    <AccordionTrigger
                                        data-testid={`faq-trigger-${i}`}
                                        className="py-6 text-left font-serif-editorial text-xl md:text-2xl text-brand-primary hover:no-underline"
                                    >
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-base text-brand-muted leading-relaxed pb-6 pr-8">
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </div>
        </section>
    );
}
